import React, { useState } from "react";
import { useMutation } from "@tanstack/react-query";
import { toast } from "sonner";
import { MapPin, Mail, Phone, Clock, Send } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { createEnquiry } from "@/lib/api";
import { CONTACT } from "@/lib/testIds";

export default function ContactPage() {
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });

  const mutation = useMutation({
    mutationFn: createEnquiry,
    onSuccess: () => {
      toast.success("Message sent!", { description: "We'll respond within one business day." });
      setForm({ name: "", email: "", phone: "", message: "" });
    },
    onError: () => toast.error("Something went wrong — please try again."),
  });

  return (
    <div>
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-24 md:pt-32 pb-16">
        <p className="overline text-secondary">Contact Us</p>
        <h1 className="mt-4 text-4xl sm:text-5xl lg:text-6xl font-serif max-w-4xl leading-tight">
          Let&apos;s talk about your building, your portfolio, or your next home.
        </h1>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pb-24 grid lg:grid-cols-12 gap-12">
        {/* Contact details */}
        <div className="lg:col-span-5 space-y-8">
          {/* Office */}
          <div className="bg-primary text-primary-foreground p-8">
            <p className="overline text-secondary">Head Office</p>
            <h2 className="mt-3 font-serif text-3xl">Bryanston, Johannesburg</h2>
            <p className="mt-2 text-primary-foreground/70 text-sm">
              Our head office and where meetings take place. Send us an enquiry
              and our team will get back to you within one business day.
            </p>
            <div className="mt-6 space-y-4 text-sm">
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 text-secondary" />
                <span>2 Bruton Road, Bryanston, 2191<br />Johannesburg, South Africa</span>
              </div>
              <a href="mailto:info@thecampusco.co.za" className="flex items-center gap-3 hover:text-secondary" data-testid="contact-general-email">
                <Mail className="w-4 h-4 text-secondary" /> info@thecampusco.co.za
              </a>
              <a href="tel:+27608362116" className="flex items-center gap-3 hover:text-secondary" data-testid="contact-headoffice-phone">
                <Phone className="w-4 h-4 text-secondary" /> 060 836 2116
              </a>
              <div className="flex items-start gap-3">
                <Clock className="w-4 h-4 mt-0.5 text-secondary" />
                <span>Mon–Fri: 08h00 – 17h00<br />Sat: By appointment</span>
              </div>
            </div>
          </div>

          <div className="aspect-[4/3] overflow-hidden border border-border">
            <iframe
              title="Campusco office location"
              src="https://www.google.com/maps?q=2+Bruton+Road,+Bryanston,+Johannesburg&output=embed"
              className="w-full h-full"
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>

        {/* Form */}
        <div className="lg:col-span-7">
          <div className="bg-background border border-border p-8 md:p-10">
            <h2 className="font-serif text-3xl">Send us a message</h2>
            <p className="mt-2 text-muted-foreground text-sm">We respond to every enquiry within one business day.</p>

            <form
              className="mt-8 space-y-5"
              onSubmit={(e) => {
                e.preventDefault();
                mutation.mutate({ ...form, enquiry_type: "general" });
              }}
            >
              <div className="grid sm:grid-cols-2 gap-5">
                <div>
                  <Label htmlFor="c-name">Full name</Label>
                  <Input id="c-name" required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1.5 rounded-sm h-11" data-testid={CONTACT.name} />
                </div>
                <div>
                  <Label htmlFor="c-email">Email</Label>
                  <Input id="c-email" type="email" required value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="mt-1.5 rounded-sm h-11" data-testid={CONTACT.email} />
                </div>
              </div>
              <div>
                <Label htmlFor="c-phone">Phone</Label>
                <Input id="c-phone" required value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="mt-1.5 rounded-sm h-11" data-testid={CONTACT.phone} />
              </div>
              <div>
                <Label htmlFor="c-message">How can we help?</Label>
                <Textarea id="c-message" required rows={6} value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="mt-1.5 rounded-sm" data-testid={CONTACT.message} />
              </div>
              <Button type="submit" disabled={mutation.isPending} className="rounded-sm h-11 px-8 bg-primary text-primary-foreground" data-testid={CONTACT.submit}>
                {mutation.isPending ? "Sending…" : (<><Send className="w-4 h-4 mr-2" /> Send Message</>)}
              </Button>
            </form>
          </div>
        </div>
      </section>
    </div>
  );
}
